import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Check, CircleCheck, Crown, Package, PackageCheck, Search, Star } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import ProfileProgressCard from '@/components/verification/ProfileProgressCard';
import PublishButton from '@/components/PublishButton';
import { MemberAvatar, Price, StatusPill } from '@/components/design';
import { BACKEND_URL } from '@/config';
import { frDate } from '@/utils/currencyUtils';
import { fmtLeft } from '@/utils/timeLeft';
import { euros, totalsByCurrency, totalsLabel } from '@/utils/walletTotals';
import { PUBLISH } from '@/utils/roles';
import { TRANSPORT_LABELS } from '@/utils/listingParsers';
import {
  SPENT_STATUSES, awaitingConfirmation, carrierTodayLine, hoursLeft, inRoute, missionSteps, nextTrip,
  pendingReceived, senderTodayLine, shortName, toConfirm, tripLoad,
} from '@/utils/dashboardItems';
import '@/styles/design-tokens.css';
import '@/styles/dashboard-c.css';

const API = `${BACKEND_URL}/api`;
const kg = (v) => `${Number(v || 0).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} kg`;
const list = (r) => (Array.isArray(r?.data) ? r.data : (r?.data?.data || []));
const none = () => ({ data: [] });
const TYPE = (p) => (p.payment_reserved ? 'Séquestre' : p.is_direct ? 'Proposition directe' : 'Négociation');
// Libellés communs sans leur emoji (règle des icônes : jamais d'emoji)
const modeLabel = (t) => (TRANSPORT_LABELS[t] || t).replace(/^\S+\s/, '');

function RouteLine({ from, to }) {
  return <span className="db-route">{from || '?'} <ArrowRight size={13} aria-hidden="true" /> {to || '?'}</span>;
}

/** Une proposition reçue en attente : qui, combien, pour quoi, avant quand. */
function PropositionTodo({ p }) {
  const left = hoursLeft(p);
  const listing = p.shipment || p.trip || {};
  return (
    <article className="db-todo" data-testid={`todo-proposition-${p.id}`}>
      <MemberAvatar firstName={p.from_user?.first_name} lastName={p.from_user?.last_name} size={40} />
      <div className="db-todo-body">
        <h3 className="db-todo-title">
          {shortName(p.from_user) || 'Un membre'} vous propose <Price amount={p.proposed_price} currency={p.currency} />
        </h3>
        <p className="db-todo-text">
          {p.shipment?.title ? `« ${p.shipment.title} » · ` : ''}
          <RouteLine from={listing.departure_city} to={listing.arrival_city} />
        </p>
        <p className="db-todo-meta">
          <span className="db-tag">{TYPE(p)}</span>
          {left != null && <span>Réponse attendue · encore {fmtLeft(left)}</span>}
        </p>
      </div>
      <Link className="db-btn" to="/app/propositions">Voir la proposition</Link>
    </article>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const isSender = user?.roles?.includes('ROLE_SENDER');
  const isCarrier = user?.roles?.includes('ROLE_CARRIER');
  const meSide = isSender && isCarrier
    ? (user?.active_role === 'ROLE_CARRIER' ? 'carrier' : 'sender')
    : (isCarrier ? 'carrier' : 'sender');
  const uid = user?.id;

  const [data, setData] = useState({ received: [], missions: [], listings: [], wallet: null, summary: null });
  const [matching, setMatching] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      const carrier = meSide === 'carrier';
      const [received, missions, listings, wallet, summary] = await Promise.all([
        axios.get(`${API}/propositions/received`, { params: { status: 'PENDING', limit: 50 } }).catch(none),
        axios.get(`${API}/missions`, { params: { limit: 50 } }).catch(none),
        axios.get(`${API}/${carrier ? 'trips' : 'shipments'}/mine`).catch(none),
        carrier ? axios.get(`${API}/payouts/wallet`).catch(() => ({ data: null })) : Promise.resolve({ data: null }),
        axios.get(`${API}/missions/summary`).catch(() => ({ data: null })),
      ]);
      if (cancel) return;
      setData({ received: list(received), missions: list(missions), listings: list(listings), wallet: wallet.data, summary: summary.data });
      setLoading(false);
    })();
    return () => { cancel = true; };
  }, [meSide]);

  const { received, missions, listings, wallet, summary } = data;
  const proposals = useMemo(() => pendingReceived(received, meSide), [received, meSide]);
  const confirms = useMemo(() => (meSide === 'sender' ? toConfirm(missions, uid) : awaitingConfirmation(missions, uid)), [missions, uid, meSide]);
  const current = useMemo(() => (meSide === 'sender' ? inRoute(missions, uid) : null), [missions, uid, meSide]);
  const trip = useMemo(() => (meSide === 'carrier' ? nextTrip(listings) : null), [listings, meSide]);

  useEffect(() => {
    if (!trip?.id) { setMatching(null); return undefined; }
    let cancel = false;
    axios.get(`${API}/trips/${trip.id}/matching`, { params: { limit: 5 } })
      .then((r) => { if (!cancel) setMatching({ items: r.data?.data || [], total: r.data?.meta?.total ?? 0 }); })
      .catch(() => { if (!cancel) setMatching(null); });
    return () => { cancel = true; };
  }, [trip?.id]);

  const todoCount = proposals.length + confirms.length + (matching?.total ? 1 : 0);
  const year = new Date().getFullYear();
  const spent = useMemo(() => totalsByCurrency(
    missions,
    (m) => m.shipper_id === uid && SPENT_STATUSES.includes(m.status) && new Date(m.created_at).getFullYear() === year,
    (m) => m.total_charged ?? m.contracted_price,
  ), [missions, uid, year]);
  const proOffer = summary?.as_carrier?.pro_offer;
  const tripMissions = trip ? missions.filter((m) => m.trip_id === trip.id && !['CANCELLED', 'DECLINED'].includes(m.status)) : [];
  const load = trip ? tripLoad(trip, missions) : null;

  return (
    <div className="db-page" data-testid="dashboard-page" data-side={meSide}>
      <header className="db-head">
        <div>
          <h1 className="db-hello">Bonjour {user?.first_name}</h1>
          <p className="db-today" data-testid="dashboard-today">
            {loading ? ' ' : meSide === 'sender' ? senderTodayLine(todoCount) : carrierTodayLine(trip)}
          </p>
          {/* Une seule note par membre, avis reçus des deux côtés : l'expéditeur la voit comme le transporteur */}
          {user?.rating_count > 0 && (
            <p className="db-rating" data-testid="dashboard-rating">
              <Star size={14} fill="currentColor" aria-hidden="true" />
              {Number(user.avg_rating || 0).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} · {user.rating_count} avis
            </p>
          )}
        </div>
        <PublishButton
          user={user}
          buttonClassName="db-publish"
          testid={isSender && isCarrier ? 'qa-publish' : meSide === 'carrier' ? 'qa-new-trip' : 'qa-new-shipment'}
        />
      </header>

      <ProfileProgressCard user={user} />

      <section className="db-section" aria-labelledby="db-todo-title" data-testid="dashboard-todo">
        <h2 className="db-h2" id="db-todo-title">À faire</h2>
        {loading ? (
          <div className="db-todo db-todo--skeleton" aria-hidden="true" />
        ) : todoCount === 0 ? (
          <div className="db-empty" data-testid="todo-empty">
            <CircleCheck size={22} aria-hidden="true" />
            <p>{meSide === 'sender' ? 'Aucune proposition à étudier ni réception à confirmer.' : 'Aucune proposition à étudier ni livraison en attente.'}</p>
            {meSide === 'sender'
              ? <Link className="db-link" to="/annonces?type=trajet">Trouver un trajet <ArrowRight size={14} aria-hidden="true" /></Link>
              : <Link className="db-link" to="/annonces?type=colis">Trouver des colis <ArrowRight size={14} aria-hidden="true" /></Link>}
          </div>
        ) : (
          <div className="db-todo-list">
            {proposals.slice(0, 2).map((p) => <PropositionTodo key={p.id} p={p} />)}
            {proposals.length > 2 && (
              <Link className="db-link" to="/app/propositions" data-testid="todo-more-propositions">
                Voir les {proposals.length - 2} autres propositions <ArrowRight size={14} aria-hidden="true" />
              </Link>
            )}

            {meSide === 'sender' && confirms.map((m) => (
              <article className="db-todo db-todo--accent" key={m.id} data-testid={`todo-confirm-${m.id}`}>
                <span className="db-todo-icon"><PackageCheck size={20} aria-hidden="true" /></span>
                <div className="db-todo-body">
                  <h3 className="db-todo-title">« {m.shipment?.title || 'Votre colis'} » est arrivé à {m.shipment?.arrival_city}</h3>
                  <p className="db-todo-text">
                    Confirmez la réception : c&apos;est ce qui déclenche le paiement de {m.carrier?.first_name || 'votre transporteur'}.
                  </p>
                </div>
                <Link className="db-btn db-btn--primary" to={`/app/missions/${m.id}`}>Confirmer la réception</Link>
              </article>
            ))}

            {meSide === 'carrier' && matching?.total > 0 && (
              <article className="db-todo" data-testid="todo-matching">
                <span className="db-todo-icon"><Package size={20} aria-hidden="true" /></span>
                <div className="db-todo-body">
                  <h3 className="db-todo-title">
                    {matching.total} colis {matching.total > 1 ? 'passent' : 'passe'} sur votre trajet du {frDate(trip.departure_date)}
                  </h3>
                  <p className="db-todo-text">
                    <RouteLine from={trip.departure_city} to={trip.arrival_city} /> · il vous reste {kg(load.free)}
                  </p>
                </div>
                <Link className="db-btn" to={`/app/trips/${trip.id}/matching`}>Voir les colis</Link>
              </article>
            )}

            {meSide === 'carrier' && confirms.map((m) => (
              <article className="db-todo" key={m.id} data-testid={`todo-awaiting-${m.id}`}>
                <MemberAvatar firstName={m.shipper?.first_name} lastName={m.shipper?.last_name} size={40} />
                <div className="db-todo-body">
                  <h3 className="db-todo-title">{shortName(m.shipper) || 'L’expéditeur'} doit confirmer la réception</h3>
                  <p className="db-todo-text">
                    « {m.shipment?.title || 'Colis'} » livré
                    {m.settlement?.carrier_net_eur != null && <> · vos {euros(m.settlement.carrier_net_eur)} vous sont versés après sa confirmation</>}
                  </p>
                </div>
                <Link className="db-btn" to={`/app/missions/${m.id}`}>Voir la mission</Link>
              </article>
            ))}
          </div>
        )}
      </section>

      {meSide === 'sender' ? (
        <section className="db-section db-card" aria-labelledby="db-route-title" data-testid="dashboard-in-route">
          <div className="db-card-head">
            <h2 className="db-h2" id="db-route-title">En route</h2>
            <Link className="db-link" to="/app/missions" data-testid="link-all-missions">Toutes mes missions <ArrowRight size={14} aria-hidden="true" /></Link>
          </div>
          {!current ? (
            <p className="db-muted">{loading ? ' ' : 'Aucun colis en route pour l’instant.'}</p>
          ) : (() => {
            const { steps, current: at } = missionSteps(current);
            return (
              <div className="db-mission" data-testid={`in-route-${current.id}`}>
                <div className="db-mission-top">
                  <div>
                    <h3 className="db-mission-title">{current.shipment?.title || 'Mon colis'}</h3>
                    <p className="db-todo-text"><RouteLine from={current.shipment?.departure_city} to={current.shipment?.arrival_city} /></p>
                  </div>
                  <StatusPill domain="mission" code={current.status} />
                </div>
                <ol className="db-steps">
                  {steps.map((s, i) => (
                    <li key={s.key} className={s.done ? 'is-done' : i === at ? 'is-current' : ''} aria-current={i === at ? 'step' : undefined}>
                      <span className="db-step-dot">{s.done && <Check size={12} aria-hidden="true" />}</span>
                      <span className="db-step-label">{s.label}</span>
                      {s.date && <span className="db-step-date">{new Date(s.date).toLocaleDateString('fr-FR')}</span>}
                    </li>
                  ))}
                </ol>
                <div className="db-mission-foot">
                  <span className="db-person">
                    <MemberAvatar firstName={current.carrier?.first_name} lastName={current.carrier?.last_name} size={32} />
                    {shortName(current.carrier) || 'Transporteur'}
                  </span>
                  <Link className="db-btn" to={`/app/missions/${current.id}`}>Voir la mission</Link>
                </div>
              </div>
            );
          })()}
        </section>
      ) : (
        <div className="db-grid-2">
          <section className="db-section db-card" aria-labelledby="db-trip-title" data-testid="dashboard-next-trip">
            <div className="db-card-head">
              <h2 className="db-h2" id="db-trip-title">Prochain départ</h2>
              <Link className="db-link" to="/app/missions" data-testid="link-all-missions">Toutes mes missions <ArrowRight size={14} aria-hidden="true" /></Link>
            </div>
            {!trip ? (
              <p className="db-muted">{loading ? ' ' : 'Aucun trajet à venir. Publiez votre prochain trajet pour recevoir des colis.'}</p>
            ) : (
              <>
                <h3 className="db-mission-title"><RouteLine from={trip.departure_city} to={trip.arrival_city} /></h3>
                <p className="db-todo-text">
                  {frDate(trip.departure_date)}
                  {(trip.transport_modes || []).length > 0 && ` · ${(trip.transport_modes || []).map(modeLabel).join(', ')}`}
                </p>
                <div className="db-load" data-testid="next-trip-load">
                  <div className="db-load-bar" role="img" aria-label={`${kg(load.reserved)} réservés sur ${kg(load.total)}`}>
                    <span style={{ width: `${load.total ? Math.round((load.reserved / load.total) * 100) : 0}%` }} />
                  </div>
                  <p className="db-load-text"><strong>{kg(load.reserved)}</strong> réservés · <strong>{kg(load.free)}</strong> libres</p>
                </div>
                {tripMissions.length > 0 && (
                  <ul className="db-mini-list">
                    {tripMissions.map((m) => (
                      <li key={m.id}>
                        <span>{m.shipment?.title || 'Colis'}{m.shipment?.weight_kg ? ` · ${kg(m.shipment.weight_kg)}` : ''}</span>
                        <StatusPill domain="mission" code={m.status} />
                      </li>
                    ))}
                  </ul>
                )}
                <div className="db-actions">
                  <Link className="db-btn" to={`/app/trips/${trip.id}`}>Voir le trajet</Link>
                  <Link className="db-btn db-btn--ghost" to={`/app/trips/${trip.id}/matching`}><Search size={14} aria-hidden="true" /> Trouver des colis</Link>
                </div>
              </>
            )}
          </section>

          <section className="db-section db-card" aria-labelledby="db-money-title" data-testid="dashboard-money">
            <h2 className="db-h2" id="db-money-title">Mon argent</h2>
            <dl className="db-money">
              <div><dt>À venir</dt><dd>{euros(wallet?.upcoming_eur)}</dd></div>
              <div><dt>Déjà versé</dt><dd>{euros(wallet?.sent_eur)}</dd></div>
              {wallet?.waiting_account_eur > 0 && <div><dt>En attente de votre compte</dt><dd>{euros(wallet.waiting_account_eur)}</dd></div>}
            </dl>
            <p className="db-muted">Virements vers votre banque chaque lundi.</p>
            <Link className="db-link" to="/app/versements">Mes versements <ArrowRight size={14} aria-hidden="true" /></Link>
            {user?.kyc_status === 'VERIFIED' && !user?.pro_subscribed && proOffer && (
              <Link className="db-pro" to="/app/abonnement" data-testid="dashboard-pro-upsell">
                <Crown size={18} aria-hidden="true" />
                <span>
                  <strong>Passez PRO</strong> : commission à {Math.round(proOffer.rate_pro * 100)} % au lieu
                  de {Math.round(proOffer.rate_standard * 100)} %, rentable dès environ {euros(proOffer.breakeven_eur)} de missions par mois.
                </span>
                <span className="db-pro-cta">{user?.pro_trial_used ? 'Découvrir' : 'Essai gratuit 7 jours'}</span>
              </Link>
            )}
          </section>
        </div>
      )}

      <section className="db-section" aria-labelledby="db-mine-title" data-testid="dashboard-listings">
        <div className="db-card-head">
          <h2 className="db-h2" id="db-mine-title">{meSide === 'sender' ? 'Mes colis' : 'Mes trajets'}</h2>
          <Link className="db-link" to={meSide === 'sender' ? '/app/shipments' : '/app/trips'}>Tout voir <ArrowRight size={14} aria-hidden="true" /></Link>
        </div>
        {!loading && listings.length === 0 ? (
          <p className="db-muted">
            {meSide === 'sender' ? 'Aucun colis publié.' : 'Aucun trajet publié.'}{' '}
            <Link className="db-link" to={meSide === 'sender' ? PUBLISH.colis.form : PUBLISH.trajet.form}>
              {meSide === 'sender' ? PUBLISH.colis.label : PUBLISH.trajet.label}
            </Link>
          </p>
        ) : (
          <div className="db-listings">
            {listings.slice(0, 6).map((l) => (
              <Link
                key={l.id}
                className="db-listing"
                to={meSide === 'sender' ? `/app/shipments/${l.id}` : `/app/trips/${l.id}`}
                data-testid={`dashboard-listing-${l.id}`}
              >
                <StatusPill domain="annonce" code={l.status} />
                <span className="db-listing-title">
                  {meSide === 'sender' ? (l.title || 'Colis') : <RouteLine from={l.departure_city} to={l.arrival_city} />}
                </span>
                <span className="db-listing-meta">
                  {meSide === 'sender'
                    ? <><RouteLine from={l.departure_city} to={l.arrival_city} />{l.weight_kg ? ` · ${kg(l.weight_kg)}` : ''}</>
                    : <>{frDate(l.departure_date)} · {kg(l.available_weight_kg)} libres</>}
                </span>
              </Link>
            ))}
          </div>
        )}
        {meSide === 'sender' && spent.length > 0 && (
          <p className="db-muted" data-testid="dashboard-spent">
            {user?.kyc_status === 'VERIFIED' && 'Compte vérifié · '}
            Dépensé en {year} : {totalsLabel(spent)}
          </p>
        )}
      </section>
    </div>
  );
}
